import { useMemo, useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { api } from '@/api/client'
import { usePageLoader } from '@/components/LogoLoader'
import { StatTile } from '@/components/StatTile'
import { useIsPhone } from '@/lib/useMediaQuery'
import { fmtEur, intlLocale, useT } from '@/i18n'
import { fleetMessages } from '@/i18n/messages/fleet'
import { INPUT } from '@/features/inventory/shared'
import { TuvText, VehicleStatusBadge, ViewSwitch } from './shared'
import { addDays, todayIso, vehicleName, type FleetBoard, type Vehicle, type VehicleStatus } from './types'

const DAYS = 14

/**
 * The fleet: AU's vehicle register with who has or has booked each vehicle, and a two-week board
 * of reservations and check-outs (docs/fleet-design.md).
 */
export function FleetPage() {
  const t = useT(fleetMessages)
  const phone = useIsPhone()
  const navigate = useNavigate()
  const [q, setQ] = useState('')
  const [status, setStatus] = useState<VehicleStatus | ''>('')
  const vehicles = useQuery<Vehicle[]>({ queryKey: ['fleet-vehicles'], queryFn: () => api.get<Vehicle[]>('/api/fleet/vehicles') })
  const from = todayIso()
  const board = useQuery<FleetBoard>({ queryKey: ['fleet-board', from], queryFn: () => api.get<FleetBoard>(`/api/fleet/board?from=${from}&days=${DAYS}`) })

  const all = vehicles.data ?? []
  const rows = useMemo(() => {
    const n = q.trim().toLowerCase()
    return all.filter((v) => (!status || v.status === status) &&
      (!n || [v.plate, v.brand, v.model, v.out?.driverName, v.out?.kolonaName, v.current?.kolonaName, v.current?.projectName]
        .some((x) => x?.toLowerCase().includes(n))))
  }, [all, q, status])

  const loader = usePageLoader(vehicles.isPending, t('loading'))
  if (loader) return loader
  const leasing = all.filter((v) => v.financing === 'leasing')

  return (
    <div className="space-y-5">
      <header className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold tracking-tight">{t('title')}</h1>
          <p className="mt-1 text-sm text-neutral-500">{t('subtitle')}</p>
        </div>
        <ViewSwitch current="list" />
      </header>

      <div className="grid grid-cols-2 gap-2 sm:gap-3 lg:grid-cols-4">
        <StatTile label={t('tileAvailable')} value={all.filter((v) => v.status === 'available').length} unit="" />
        <StatTile label={t('tileReserved')} value={all.filter((v) => v.status === 'reserved').length} unit="" />
        <StatTile label={t('tileOut')} value={all.filter((v) => v.status === 'out').length} unit="" />
        <StatTile label={t('tileLeasing', { count: leasing.length })} value={leasing.reduce((s, v) => s + (v.monthlyPayment ?? 0), 0)} unit="EUR" />
      </div>

      {board.data && <Board board={board.data} vehicles={all} onOpen={(id) => navigate(`/fleet/${id}`)} />}

      <div className="flex flex-wrap items-center gap-2">
        <input type="search" value={q} onChange={(e) => setQ(e.target.value)} placeholder={t('search')} aria-label={t('search')}
          className={`${INPUT} basis-full sm:basis-80`} />
        {(['', 'available', 'reserved', 'out'] as const).map((s) => (
          <button key={s || 'all'} type="button" onClick={() => setStatus(s)}
            className={`min-h-9 rounded-full px-3 text-xs font-medium ring-1 ${status === s
              ? 'bg-neutral-900 text-white ring-neutral-900 dark:bg-white dark:text-neutral-900'
              : 'text-neutral-600 ring-neutral-300 hover:bg-neutral-50 dark:text-neutral-300 dark:ring-neutral-700 dark:hover:bg-neutral-800'}`}>
            {s === '' ? t('all') : s === 'available' ? t('statusAvailable') : s === 'reserved' ? t('statusReserved') : t('statusOut')}
          </button>
        ))}
        <span className="ml-auto text-xs text-neutral-500">{t('shownOf', { shown: rows.length, total: all.length })}</span>
      </div>

      {phone ? (
        <ul className="space-y-2">
          {rows.slice(0, 200).map((v) => (
            <li key={v.id}>
              <Link to={`/fleet/${v.id}`} className="block rounded-2xl bg-white p-3 ring-1 ring-neutral-200 dark:bg-neutral-900 dark:ring-neutral-800">
                <div className="flex items-center justify-between gap-2">
                  <span className="font-mono text-sm font-semibold">{v.plate}</span>
                  <VehicleStatusBadge status={v.status} />
                </div>
                <div className="mt-0.5 text-sm">{vehicleName(v) || '—'}</div>
                <div className="mt-0.5 truncate text-xs text-neutral-500"><Holder v={v} /></div>
              </Link>
            </li>
          ))}
        </ul>
      ) : (
        <div className="overflow-x-auto rounded-2xl bg-white ring-1 ring-neutral-200 dark:bg-neutral-900 dark:ring-neutral-800">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-neutral-200 text-left text-xs text-neutral-500 dark:border-neutral-800">
                <th className="px-3 py-2 font-medium">{t('colPlate')}</th>
                <th className="px-3 py-2 font-medium">{t('colVehicle')}</th>
                <th className="px-3 py-2 font-medium">{t('colStatus')}</th>
                <th className="px-3 py-2 font-medium">{t('colWith')}</th>
                <th className="px-3 py-2 font-medium">{t('colTuv')}</th>
                <th className="px-3 py-2 font-medium">{t('colFinancing')}</th>
              </tr>
            </thead>
            <tbody>
              {rows.slice(0, 300).map((v) => (
                <tr key={v.id} onClick={() => navigate(`/fleet/${v.id}`)}
                  className="cursor-pointer border-b border-neutral-100 last:border-0 hover:bg-neutral-50 dark:border-neutral-800/60 dark:hover:bg-neutral-800/40">
                  <td className="whitespace-nowrap px-3 py-2 font-mono font-semibold">{v.plate}</td>
                  <td className="px-3 py-2">{vehicleName(v) || '—'}<div className="text-xs text-neutral-500">{v.vehicleType}</div></td>
                  <td className="px-3 py-2"><VehicleStatusBadge status={v.status} /></td>
                  <td className="px-3 py-2 text-xs"><Holder v={v} /></td>
                  <td className="px-3 py-2 text-xs"><TuvText due={v.tuvDue} /></td>
                  <td className="px-3 py-2 text-xs">
                    {v.financing === 'leasing' ? <>{t('leasing')}{v.monthlyPayment ? ` · ${fmtEur(v.monthlyPayment)}/${t('month')}` : ''}</> : v.financing === 'paid' ? t('owned') : '—'}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      <p className="text-xs text-neutral-400">{t('registerNote')}</p>
    </div>
  )
}

function Holder({ v }: { v: Vehicle }) {
  const t = useT(fleetMessages)
  if (v.out) return <>{t('withDriver', { name: v.out.driverName })}{v.out.kolonaName ? ` · ${v.out.kolonaName}` : ''}{v.out.projectName ? ` · ${v.out.projectName}` : ''}</>
  if (v.current) return <>{[v.current.kolonaName, v.current.projectName].filter(Boolean).join(' · ')}</>
  if (v.next) return <span className="text-neutral-400">{t('nextFrom', { date: fmtDay(v.next.startsOn) })}</span>
  return <span className="text-neutral-400">—</span>
}

const fmtDay = (iso: string) => { const [, m, d] = iso.split('-'); return `${d}.${m}.` }

/** Two weeks, one row per vehicle with a booking or a trip: who has it, when. */
function Board({ board, vehicles, onOpen }: { board: FleetBoard; vehicles: Vehicle[]; onOpen: (id: string) => void }) {
  const t = useT(fleetMessages)
  const days = Array.from({ length: DAYS }, (_, i) => addDays(board.from, i))
  const ids = [...new Set([...board.trips.map((x) => x.vehicleId), ...board.reservations.map((x) => x.vehicleId)])]
  const byId = new Map(vehicles.map((v) => [v.id, v]))
  const col = (iso: string) => Math.max(0, Math.min(DAYS, Math.round((Date.parse(iso) - Date.parse(board.from)) / 86_400_000)))
  const wd = new Intl.DateTimeFormat(intlLocale(), { weekday: 'short' })
  if (ids.length === 0) return <p className="rounded-2xl bg-neutral-50 p-4 text-sm text-neutral-500 dark:bg-neutral-900">{t('boardEmpty')}</p>
  return (
    <section className="space-y-2">
      <h2 className="text-lg font-semibold">{t('boardTitle')}</h2>
      <div className="overflow-x-auto rounded-2xl bg-white p-3 ring-1 ring-neutral-200 dark:bg-neutral-900 dark:ring-neutral-800">
        <div className="grid min-w-[52rem] gap-y-1.5" style={{ gridTemplateColumns: `9rem repeat(${DAYS}, minmax(0, 1fr))` }}>
          <div />
          {days.map((d) => (
            <div key={d} className={`text-center text-[10px] leading-tight text-neutral-500 ${[0, 6].includes(new Date(`${d}T12:00:00Z`).getUTCDay()) ? 'bg-neutral-50 dark:bg-neutral-800/40' : ''}`}>
              {wd.format(new Date(`${d}T12:00:00Z`))}<br />{fmtDay(d)}
            </div>
          ))}
          {ids.map((id) => {
            const v = byId.get(id)
            return (
              <div key={id} className="contents">
                <button type="button" onClick={() => onOpen(id)} className="truncate pr-2 text-left font-mono text-xs font-semibold hover:underline">
                  {v?.plate ?? '—'}
                </button>
                <div className="relative h-8" style={{ gridColumn: `2 / span ${DAYS}` }}>
                  {board.reservations.filter((r) => r.vehicleId === id).map((r) => {
                    const a = col(r.startsOn), b = col(addDays(r.endsOn, 1))
                    return (
                      <div key={r.id} title={`${r.kolonaName ?? ''} ${r.projectName ?? ''}`}
                        className="absolute inset-y-0 truncate rounded-md bg-violet-100 px-1.5 text-[11px] leading-8 text-violet-900 ring-1 ring-violet-300 dark:bg-violet-500/20 dark:text-violet-200 dark:ring-violet-500/40"
                        style={{ left: `${(a / DAYS) * 100}%`, width: `${((b - a) / DAYS) * 100}%` }}>
                        {r.kolonaName ?? r.projectName}
                      </div>
                    )
                  })}
                  {board.trips.filter((x) => x.vehicleId === id).map((x) => {
                    const a = col(x.outAt.slice(0, 10)), b = col(addDays((x.inAt ?? todayIso()).slice(0, 10), 1))
                    return (
                      <div key={x.id} title={x.driverName}
                        className="absolute bottom-0 h-2.5 rounded-full bg-amber-500"
                        style={{ left: `${(a / DAYS) * 100}%`, width: `${(Math.max(b - a, 1) / DAYS) * 100}%` }} />
                    )
                  })}
                </div>
              </div>
            )
          })}
        </div>
        <div className="mt-2 flex gap-4 text-xs text-neutral-500">
          <span className="inline-flex items-center gap-1.5"><span className="inline-block h-3 w-4 rounded-sm bg-violet-200 ring-1 ring-violet-300" />{t('legendReserved')}</span>
          <span className="inline-flex items-center gap-1.5"><span className="inline-block h-2 w-4 rounded-full bg-amber-500" />{t('legendOut')}</span>
        </div>
      </div>
    </section>
  )
}
